package com.swetdot.swetgram.data.media

import com.swetdot.swetgram.protocol.dto.MediaPurpose
import com.swetdot.swetgram.protocol.dto.UploadLimits
import kotlin.math.roundToInt

/**
 * How the pickers encode what they produce.
 *
 * The BOUNDS are not here -- `UploadLimits` in `:protocol` owns the byte ceiling, the MIME set
 * and the pixel caps, so the two ends cannot disagree about what "too large" means. This object
 * holds only the client-side encoding choice, which the contract has no opinion about.
 */
object ImageEncoding {
    /**
     * What both pickers emit, whatever went in.
     *
     * JPEG rather than WebP even though DECISIONS §10 names both: Android can encode WebP from
     * `Bitmap.compress`, iOS has no equivalent one-liner, and a format that only one of the two
     * clients can produce is not a shared decision -- it is two behaviours with one name.
     *
     * Re-encoding is also what makes `exif_removed: true` honest. Neither `Bitmap.compress` nor
     * `UIImageJPEGRepresentation` copies the source's metadata, so the EXIF block -- including
     * the GPS tags that say where the user's home is -- does not survive the round trip. Nothing
     * has to remember to strip it, because there is nothing to strip.
     */
    const val OUTPUT_MIME_TYPE: String = "image/jpeg"

    /** DECISIONS §10: "JPEG/WebP quality target: 82-85%". */
    const val JPEG_QUALITY: Int = 85
}

/**
 * What the image is for.
 *
 * A `:shared` enum in front of `:protocol`'s [MediaPurpose], per IMPLEMENTATION_NOTES §10. It is
 * not ceremony: the wire enum carries an `UNKNOWN` member so an older build can read a purpose a
 * later server introduces, `UploadLimits.maxEdgeFor` answers null for it, and a picker asked to
 * resize for a purpose with no cap has nothing to do. Removing that case at the boundary is what
 * lets [maxEdgePx] be a plain Int instead of something every call site has to null-check.
 *
 * The caps themselves come from `:protocol` rather than being retyped here; DECISIONS §10 is
 * where they are decided ("Avatar maximum: 1024x1024. Chat image maximum: 2560 px on the longest
 * side"), and a group avatar is an avatar.
 */
enum class UploadPurpose(
    /** Internal so a screen cannot reach a wire enum through this. */
    internal val wire: MediaPurpose,
    val maxEdgePx: Int,
) {
    AVATAR(MediaPurpose.AVATAR, UploadLimits.AVATAR_MAX_EDGE),
    CHAT_IMAGE(MediaPurpose.CHAT_IMAGE, UploadLimits.CHAT_IMAGE_MAX_EDGE),
    CHAT_VOICE(MediaPurpose.CHAT_VOICE, 1),
    GROUP_AVATAR(MediaPurpose.GROUP_AVATAR, UploadLimits.AVATAR_MAX_EDGE),
    PICSO_PHOTO(MediaPurpose.PICSO_PHOTO, UploadLimits.PICSO_PHOTO_MAX_EDGE),
}

/** A width and a height, in pixels. */
data class ImageSize(val width: Int, val height: Int)

/**
 * The size [width] x [height] becomes when its longest edge is capped at [maxEdgePx].
 *
 * Never upscales: a 300px avatar stays 300px rather than being blown up to 1024 and uploaded at
 * four times the bytes for none of the detail. Aspect ratio is preserved from the ORIGINAL pair
 * rather than by scaling each edge independently, which is what stops a panorama arriving square.
 *
 * Pure and in commonMain so both pickers resize by the same rule, and so the rule is testable
 * without a device.
 */
fun fitWithin(width: Int, height: Int, maxEdgePx: Int): ImageSize {
    require(maxEdgePx > 0) { "maxEdgePx must be positive" }
    // A decoder that could not read the header reports 0 or -1. Clamping rather than throwing
    // keeps a broken image a failed upload instead of a crash inside a decode callback.
    if (width <= 0 || height <= 0) {
        return ImageSize(width.coerceAtLeast(1), height.coerceAtLeast(1))
    }
    val longest = maxOf(width, height)
    if (longest <= maxEdgePx) return ImageSize(width, height)

    val scale = maxEdgePx.toDouble() / longest.toDouble()
    return ImageSize(
        width = (width * scale).roundToInt().coerceAtLeast(1),
        height = (height * scale).roundToInt().coerceAtLeast(1),
    )
}

/**
 * An image the user chose, already normalised: downscaled, re-encoded, stripped of metadata.
 *
 * Deliberately NOT a data class. `ByteArray` has referential `equals`, so a generated `equals`
 * would compare two identical images as different and a generated `hashCode` would change per
 * instance -- both silently, and both only visible once something put one in a `Set`.
 *
 * There is no `filename` here on purpose. DECISIONS §10 requires object keys that "must not
 * contain email, username, or original filename", and the original name is exactly the field
 * that carries a person's name out of their photo library -- `IMG_wedding_anna.jpg`. The name
 * sent to `createPresignedUpload` is generated in [UploadRepository] instead, so there is no
 * point in this client where the original one exists to be leaked.
 */
class PickedImage(
    val bytes: ByteArray,
    val mimeType: String,
    val width: Int,
    val height: Int,
) {
    val sizeBytes: Long get() = bytes.size.toLong()
}

/**
 * Where a normalised image comes from.
 *
 * An interface in front of [ImagePicker] for the reason
 * [SessionStorage][com.swetdot.swetgram.data.auth.SessionStorage] gives: an `expect class`
 * cannot be substituted in `commonTest`, so everything that consumes a picked image would be
 * untestable if callers depended on the expect directly.
 */
fun interface ImageSource {
    /**
     * Shows the platform's image chooser and returns what came back.
     *
     * Null means the user dismissed it, which is not a failure and must not be reported as one.
     * [purpose] decides the resize cap only; the chooser looks the same either way.
     */
    suspend fun pick(purpose: UploadPurpose): PickedImage?
}

/**
 * The platform picker.
 *
 * Both actuals take a callback that produces the platform's own native image -- a `content://`
 * `Uri` on Android, a `UIImage` on iOS -- and do the resize, alpha flattening and re-encode
 * themselves.
 *
 * That split is forced, and it is worth being explicit about why rather than treating it as an
 * accident of wiring. Presenting the OS chooser needs a host that `:shared` does not own and
 * cannot reach: an `Activity` registered through `ActivityResultRegistry` on Android, a
 * `UIViewController` to present `PHPickerViewController` from on iOS. What `:shared` CAN own is
 * the part the contract has an opinion about -- the size cap, the quality, and the removal of
 * EXIF and GPS. Putting that in the app targets instead would mean writing it twice and having
 * it drift, which is the failure `exif_removed: true` would then quietly assert its way past.
 */
expect class ImagePicker : ImageSource {
    /*
     * Redeclared from [ImageSource] for the reason spelled out on `SessionStore`: an `expect
     * class` that only inherits an interface compiles for each platform, where an actual is
     * visible, but `compileCommonMainKotlinMetadata` sees no actual and rejects the class as
     * having unimplemented members.
     */
    override suspend fun pick(purpose: UploadPurpose): PickedImage?
}
